/*
 * File: Fundraising
 * Project: a-a
 * File Created: Monday, 23rd June 2025 3:05:07 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 17th October 2025 8:17:38 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useEffect, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CustomTextField from '../../../CustomTextField/CustomTextField';
import { useInput } from '../../../../../customHook/useInput';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from '../../../CustomAutocomplete/CustomAutocomplete';
import zeplinColor from '../../../../../theme/Color';
import dayjs, { Dayjs } from 'dayjs';
import CustomDatePicker from '../../../CustomDatePicker/CustomDatePicker';
import CustomCheckbox from '../../../CustomCheckbox/CustomCheckbox';
import { ClientService } from '../../../../../service/client.service';
import { Utilities } from '../../../../../utilities/utilities';
const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
      // padding: '16px',
    },
  },
};
const FundraisingFormField = ({
  handleInputChange,
  value,
  dealType,
  setIsValid,
}: {
  handleInputChange: (field: string, value: any) => void;
  dealType: string;
  value: any;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const {
    handleChange: handleOpportunityChange,
    handleBlur: handleOpportunityBlur,
    clearInput: clearOpportunity,
    value: Opportunity,
    invalid: invalidOpportunity,
    didChange: didOpportunityChange,
  } = useInput(value?.name ? value?.name : '', () => true);
  const {
    handleChange: handleAmountChange,
    handleBlur: handleAmountBlur,
    clearInput: clearAmount,
    value: amount,
    invalid: invalidAmount,
    didChange: didAmountChange,
  } = useInput(value?.amount ? value?.amount : '', () => true);
  const {
    handleChange: handleProbabilityChange,
    handleBlur: handleProbabilityBlur,
    clearInput: clearProbability,
    value: probability,
    invalid: invalidProbability,
    didChange: didProbabilityChange,
  } = useInput(value?.probability ? value?.probability : '', () => true);
  const {
    handleChange: handleOverviewChange,
    handleBlur: handleOverviewBlur,
    clearInput: clearOverview,
    value: overview,
    invalid: invalidOverview,
    didChange: didOverviewChange,
  } = useInput(value?.overview ? value?.overview : '', () => true);

  const {
    handleChange: handleEstimatedChange,
    handleBlur: handleEstimatedBlur,
    clearInput: clearEstimated,
    value: estimated,
    invalid: invalidEstimated,
    didChange: didEstimatedChange,
  } = useInput(
    value?.gdEstimatedValuation
      ? value?.gdEstimatedValuation
      : value?.estimatedValuation
      ? value?.estimatedValuation
      : '',
    () => true,
  );

  const [closeDate, setCloseDate] = useState<Dayjs | null>(
    value?.closeDate ? dayjs(value?.closeDate) : null,
  );

  const [selectedItems, setSelectedItems] = useState<{
    account: any[];
    stageName: string[];
    exclusivity: string[];
    type: string[];
    category: string[];
    gdCommission: string[];
    lostReason: string[];
    leadSource: string[];
    campaignId: string[]; // Added for Primary Campaign Source
  }>({
    account: value?.accountId ? value?.accountId : [],
    stageName: value?.stageName ? [value?.stageName] : [],
    campaignId: value?.campaignId ? [value?.campaignId] : [], // Added for Primary Campaign Source
    exclusivity: value?.exclusivity ? [value?.exclusivity] : [],
    type: value?.type ? [value?.type] : [],
    category: value?.category ? [value?.category] : [],
    gdCommission: value?.gdCommission
      ? [value?.gdCommission]
      : value?.commission
      ? [value?.commission]
      : [],
    lostReason: value?.lostReason ? [value?.lostReason] : [],
    leadSource: value?.leadSource ? [value?.leadSource] : [],
  });
  const [drowdownList, setDropdownList] = useState<any>({});

  const getDropdown = async () => {
    try {
      const res = await ClientService.getDealDropdown(dealType);
      console.log(res);
      setDropdownList(res);
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    getDropdown();
  }, []);

  useEffect(() => {
    const valid =
      !Opportunity ||
      !selectedItems?.stageName?.length ||
      !closeDate ||
      (selectedItems?.stageName[0] === 'Closed Lost' && !selectedItems?.lostReason);
    setIsValid(valid);
  }, [Opportunity, selectedItems, closeDate]);

  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };

  return (
    <>
      <Typography variant='heading4'>Opportunity Information</Typography>
      <CustomTextField
        style={{ width: '100%' }}
        form={true}
        onBlur={handleOpportunityBlur}
        onChange={(e) => {
          handleOpportunityChange(e);
          handleInputChange('name', e.target.value.trim());
        }}
        name='Opportunity name'
        text={Opportunity}
        error={invalidOpportunity && didOpportunityChange ? 'Please enter valid Opportunity' : ''}
        label={'Opportunity name'}
        type='text'
        tabindex={1}
        required
      />
      {/* <FormControl sx={{ width: '100%' }}>
        <CustomAutocomplete
          required={true}
          options={[{ Name: 'Marketing', Id: '1' }]}
          getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
          label={'Account name'}
          handleDeleteItems={() => {}}
          selectedItems={selectedItems}
          handleSelection={(value) => {
            handleSelection(value, 'account');
            handleInputChange('accoutId', value);
          }}
          value={selectedItems?.account[0]}
          handleFilter={() => {}}
          placeholder='Search people'
          currentLength={[{ Name: 'Marketing', Id: '1' }]?.length}
          totalLength={[{ Name: 'Marketing', Id: '1' }]?.length}
          next={() => {}}
          multiple={false}
        />
      </FormControl> */}
      <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Stage<span style={{ color: zeplinColor.Error }}>*</span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.stageName}
            onChange={(e) => {
              handleSelection(e, 'stageName');
              handleInputChange('stageName', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Stage'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.StageName?.length &&
                    drowdownList?.StageName.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.StageName.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.StageName?.length &&
              drowdownList?.StageName.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.stageName?.length
                        ? selectedItems?.stageName?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <CustomDatePicker
          value={closeDate}
          size={isXs ? 'small' : 'medium'}
          setValue={(date) => {
            setCloseDate(date); // Update local state
            handleInputChange('closeDate', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          label={'Close date'}
          required={true}
        />
      </Box>
      {/* <CustomAutocomplete
        required={false}
        options={drowdownList?.campaignIdList || []} // Updated options for Primary Campaign Source
        getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
        label={'Primary Campaign Source'} // Updated label
        handleDeleteItems={() => {}}
        selectedItems={selectedItems}
        handleSelection={(value) => {
          handleSelection(value, 'campaignId'); // Updated key for selection
          handleInputChange('campaignId', value);
        }}
        value={selectedItems?.campaignId?.[0]} // Updated value for Primary Campaign Source
        handleFilter={() => {}}
        placeholder='Search campaign sources' // Updated placeholder
        currentLength={drowdownList?.campaignIdList?.length || []?.length}
        totalLength={drowdownList?.campaignIdList?.length || []?.length}
        next={() => {}}
        multiple={false}
      /> */}
      <FormControl sx={{ width: '100%' }}>
        <InputLabel
          id='demo-multiple-name-label'
          size={isXs ? 'small' : 'medium'}
          sx={{
            '&.Mui-focused': { color: zeplinColor.Neutral9 },
            fontSize: isXs ? '14px' : '16px',
          }}
        >
          Primary Campaign Source<span style={{ color: zeplinColor.Error }}>*</span>
        </InputLabel>
        <Select
          labelId='demo-multiple-name-label'
          size={isXs ? 'small' : 'medium'}
          id='demo-multiple-name'
          // multiple
          required={true}
          value={selectedItems.campaignId}
          onChange={(e) => {
            handleSelection(e, 'campaignId');
            handleInputChange('campaignId', e.target.value);
          }}
          input={
            <OutlinedInput
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  color: zeplinColor.Neutral9,
                  borderColor: zeplinColor.Neutral9,
                },
              }}
              label='Primary Campaign Source'
            />
          }
          renderValue={
            (selected) =>
              // (selected as string[]).map((val) => (
              {
                return selected?.length && drowdownList?.campaigns ? (
                  Object.keys(drowdownList?.campaigns)?.length &&
                  Utilities.getMatchingRecordTypeKey(selected[0], drowdownList?.campaigns) ? (
                    Utilities.getMatchingRecordTypeKey(selected[0], drowdownList?.campaigns)
                  ) : (
                    <Skeleton
                      variant='text'
                      sx={{
                        fontSize: theme.typography.body2,
                        width: '100%',
                        // Optionally adjust other styles here
                      }}
                    />
                  )
                ) : (
                  ''
                );
              }
            // ))
          }
          MenuProps={MenuProps}
          IconComponent={KeyboardArrowDown}
        >
          {drowdownList?.campaigns &&
            Object.keys(drowdownList?.campaigns)?.length &&
            Object.keys(drowdownList?.campaigns)?.map((name: any) => (
              <MenuItem key={drowdownList?.campaigns[name]} value={drowdownList?.campaigns[name]}>
                <Radio
                  checked={
                    selectedItems?.campaignId?.length
                      ? selectedItems?.campaignId?.indexOf(drowdownList?.campaigns[name]) > -1
                      : false
                  } // Check if this radio should be selected
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': {
                      color: zeplinColor.BasicBlack,
                    },
                  }}
                />
                {name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, width: '100%' }}>
        <CustomTextField
          disable={true}
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handleAmountBlur && handleAmountBlur();
          }}
          onChange={(e) => {
            handleAmountChange && handleAmountChange(e);
            handleInputChange('amount', +e.target.value);
          }}
          name={'amount'}
          text={amount ? amount : ''}
          error={invalidAmount && didAmountChange ? 'Please enter valid Amount' : ''}
          label={'Amount'}
          type='number'
          tabindex={1}
          // required
        />
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.type}
            onChange={(e) => {
              handleSelection(e, 'type');
              handleInputChange('type', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Type'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Type?.length &&
                    drowdownList?.Type.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.Type.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Type?.length &&
              drowdownList?.Type?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.type?.length ? selectedItems?.type?.indexOf(name) > -1 : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? '14px' : '16px',
            }}
          >
            Commission (%)<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.gdCommission}
            onChange={(e) => {
              handleSelection(e, 'gdCommission');
              handleInputChange('gdCommission', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Commision (%)'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.GD_Commission__c?.length &&
                    drowdownList?.GD_Commission__c.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.GD_Commission__c.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.GD_Commission__c?.length &&
              drowdownList?.GD_Commission__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.gdCommission?.length
                        ? selectedItems?.gdCommission?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <CustomTextField
          style={{ width: '100%' }}
          form={true}
          onBlur={() => {
            handleProbabilityBlur && handleProbabilityBlur();
          }}
          onChange={(e) => {
            handleProbabilityChange && handleProbabilityChange(e);
            handleInputChange('probability', +e.target.value);
          }}
          name={'Probability %'}
          text={probability ? probability : ''}
          error={invalidProbability && didProbabilityChange ? 'Please enter valid Probability' : ''}
          label={'Probability %'}
          type='number'
          tabindex={2}
          max={100}
          // required
        />
      </Box>
      {selectedItems?.stageName.includes('Closed Lost') && (
        <>
          <Typography variant='heading4'>Additional Information</Typography>
          <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel
                size={isXs ? 'small' : 'medium'}
                id='demo-multiple-name-label'
                sx={{
                  '&.Mui-focused': { color: zeplinColor.Neutral9 },
                  fontSize: isXs ? '14px' : '16px',
                }}
              >
                Reason Lost<span style={{ color: zeplinColor.Error }}></span>
              </InputLabel>
              <Select
                labelId='demo-multiple-name-label'
                size={isXs ? 'small' : 'medium'}
                id='demo-multiple-name'
                // multiple
                required={true}
                value={selectedItems.lostReason}
                onChange={(e) => {
                  handleSelection(e, 'lostReason');
                  handleInputChange('reasonLost', e.target.value);
                }}
                input={
                  <OutlinedInput
                    sx={{
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                        color: zeplinColor.Neutral9,
                        borderColor: zeplinColor.Neutral9,
                      },
                    }}
                    label='lostReason'
                  />
                }
                renderValue={
                  (selected) =>
                    // (selected as string[]).map((val) => (
                    {
                      return selected?.length ? (
                        drowdownList?.Reason_Lost__c?.length &&
                        drowdownList?.Reason_Lost__c?.filter(
                          (val: any) => val === selected[0],
                        )[0] ? (
                          drowdownList?.Reason_Lost__c?.filter((val: any) => val === selected[0])[0]
                        ) : (
                          <Skeleton
                            variant='text'
                            sx={{
                              fontSize: theme.typography.body2,
                              width: '100%',
                              // Optionally adjust other styles here
                            }}
                          />
                        )
                      ) : (
                        ''
                      );
                    }
                  // ))
                }
                MenuProps={MenuProps}
                IconComponent={KeyboardArrowDown}
              >
                {drowdownList?.Reason_Lost__c?.length &&
                  drowdownList?.Reason_Lost__c?.map((name: any) => (
                    <MenuItem key={name} value={name}>
                      <Radio
                        checked={
                          selectedItems?.lostReason?.length
                            ? selectedItems?.lostReason?.indexOf(name) > -1
                            : false
                        } // Check if this radio should be selected
                        sx={{
                          color: zeplinColor.BasicBlack,
                          '&.Mui-checked': {
                            color: zeplinColor.BasicBlack,
                          },
                        }}
                      />
                      {name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
      <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, width: '100%' }}>
        {/* <CustomTextField
          style={{ width: '100%' }}
          onBlur={() => {
            handleEstimatedBlur && handleEstimatedBlur();
          }}
          onChange={(e) => {
            handleEstimatedChange && handleEstimatedChange(e);
            handleInputChange('gdEstimatedValuation', +e.target.value);
          }}
          name={'Estimated Valuation'}
          text={estimated ? estimated : ''}
          error={invalidEstimated && didEstimatedChange ? 'Please enter valid Estimated' : ''}
          label={'Estimated Valuation'}
          type='number'
          tabindex={1}
          // max={100}
          // required
        /> */}
        {/* <CustomTextField
          style={{ width: '100%' }}
          onBlur={() => {
            handlePartnerPercentBlur && handlePartnerPercentBlur();
          }}
          onChange={(e) => {
            handlePartnerPercentChange && handlePartnerPercentChange(e);
          }}
          name={'Partner percent (%)'}
          text={partnerPercent ? partnerPercent : ''}
          error={
            invalidPartnerPercent && didPartnerPercentChange
              ? 'Please enter valid PartnerPercent'
              : ''
          }
          label={'Partner percent (%)'}
          type='number'
          tabindex={1}
          // required
        /> */}
      </Box>
      <Typography variant='heading4'>Description Information</Typography>
      <CustomTextField
        style={{ width: '100%' }}
        form={true}
        onBlur={handleOverviewBlur}
        onChange={(e) => {
          handleOverviewChange(e);
          handleInputChange('overview', e.target.value);
        }}
        name='Overview'
        text={overview}
        error={invalidOverview && didOverviewChange ? 'Please enter valid Overview' : ''}
        label={'Overview'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
    </>
  );
};

export default FundraisingFormField;
